import { createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';

import { spenderEntityIds as spenderEntitiesOf } from '../admin/perms.ts';
import { api } from '../api/index.ts';
import type { Entity, Entry, Me, PendingCashIn, Wallet } from '../api/types.ts';
import { applyOrder, loadOrder, move, saveOrder } from '../lib/entityOrder.ts';
import type { RecentSave } from './draft.ts';

const LAST_ENTITY_KEY = 'expense.lastEntity';

interface SpenderState {
  me: Me;
  entities: Entity[];
  entityById: (id: string) => Entity | undefined;
  wallets: Wallet[];
  balanceOf: (entityId: string) => number;
  cashIns: PendingCashIn[];
  entries: Entry[];
  recentSaves: RecentSave[];
  lastEntityId: string;
  loading: boolean;
  refresh: () => Promise<void>;
  rememberSave: (save: RecentSave) => void;
  setLastEntity: (id: string) => void;
  /** Arrange: move a wallet one place earlier (-1) or later (+1); saved per user. */
  moveEntity: (id: string, dir: -1 | 1) => void;
  /** Drag and drop: save the whole new order. */
  setEntityOrder: (ids: string[]) => void;
  resetOrder: () => void;
  isCustomOrder: boolean;
}

function browserStorage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

const Ctx = createContext<SpenderState | null>(null);

export function SpenderProvider({ me, children }: { me: Me; children: ReactNode }) {
  // Office boys, and roles with "Records own purchases" (they spend from a wallet too).
  const spenderEntityIds = useMemo(() => spenderEntitiesOf(me), [me]);
  // The spender's own arrangement (Arrange on Home), applied everywhere wallets are listed.
  const [order, setOrder] = useState<string[]>(() => loadOrder(browserStorage(), me.id));
  const entities = useMemo(
    () =>
      applyOrder(
        me.entities.filter((e) => spenderEntityIds.has(e.id)),
        order,
      ),
    [me, spenderEntityIds, order],
  );
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [cashIns, setCashIns] = useState<PendingCashIn[]>([]);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [recentSaves, setRecentSaves] = useState<RecentSave[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastEntityId, setLastEntityId] = useState<string>(() => {
    try {
      return localStorage.getItem(LAST_ENTITY_KEY) ?? '';
    } catch {
      return '';
    }
  });

  const refresh = useCallback(async () => {
    const [w, c, e] = await Promise.all([api.wallets(), api.pendingCashIns(), api.myEntries()]);
    // Someone who also uses the owner app there is sent every wallet: keep their own.
    setWallets(w.filter((x) => spenderEntityIds.has(x.entityId) && (x.userId === undefined || x.userId === me.id)));
    setCashIns(c);
    setEntries(e);
    setLoading(false);
  }, [spenderEntityIds, me.id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<SpenderState>(
    () => ({
      me,
      entities,
      entityById: (id) => entities.find((e) => e.id === id),
      wallets,
      balanceOf: (id) => wallets.find((w) => w.entityId === id)?.balance ?? 0,
      cashIns,
      entries,
      recentSaves,
      lastEntityId: entities.some((e) => e.id === lastEntityId) ? lastEntityId : (entities[0]?.id ?? ''),
      loading,
      refresh,
      rememberSave: (s) => setRecentSaves((prev) => [s, ...prev].slice(0, 20)),
      setLastEntity: (id) => {
        setLastEntityId(id);
        try {
          localStorage.setItem(LAST_ENTITY_KEY, id);
        } catch {
          /* ignore */
        }
      },
      moveEntity: (id, dir) => {
        const next = move(
          entities.map((e) => e.id),
          id,
          dir,
        );
        setOrder(next);
        saveOrder(browserStorage(), me.id, next);
      },
      setEntityOrder: (ids) => {
        setOrder(ids);
        saveOrder(browserStorage(), me.id, ids);
      },
      resetOrder: () => {
        setOrder([]);
        saveOrder(browserStorage(), me.id, []);
      },
      isCustomOrder: order.length > 0,
    }),
    [me, entities, wallets, cashIns, entries, recentSaves, lastEntityId, loading, refresh, order],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSpender(): SpenderState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSpender outside SpenderProvider');
  return v;
}

function subscribeOnline(cb: () => void): () => void {
  window.addEventListener('online', cb);
  window.addEventListener('offline', cb);
  return () => {
    window.removeEventListener('online', cb);
    window.removeEventListener('offline', cb);
  };
}

export function useOnline(): boolean {
  return useSyncExternalStore(subscribeOnline, () => navigator.onLine);
}
